import { RRule, rrulestr } from "rrule";
import { fromIso } from "../time.js";
import { floatingToInstant, instantToFloating } from "../zone.js";
import type { RecurrenceRule } from "./types.js";

/**
 * Build the rrule.js object for a rule, given the dtstart/until to expand
 * against. `untilBoundary` is the EXCLUSIVE end (first moment NOT included);
 * rrule.js UNTIL is inclusive, so we subtract 1 ms.
 *
 * In zone mode the caller passes *floating* dtstart/until (UTC fields holding
 * wall-clock in the rule's tz). rrule.js does pure UTC-field arithmetic, so a
 * 09:00 daily series stays 09:00 in floating space every day — DST is applied
 * only when each occurrence is re-anchored to a true instant.
 */
function buildRRule(rruleStr: string, dtstart: Date, untilBoundary: Date | null): RRule {
  const r = rrulestr(rruleStr, { dtstart });
  if (!(r instanceof RRule)) throw new Error("Compound RRULE not supported in v1");
  if (!untilBoundary) return r;
  return new RRule({ ...r.options, until: new Date(untilBoundary.getTime() - 1) });
}

/** Floating dtstart/until views for a zoned rule. */
function floatingView(rule: RecurrenceRule, tz: string): { dtstart: Date; until: Date | null } {
  return {
    dtstart: instantToFloating(rule.dtstart, tz),
    until: rule.until ? instantToFloating(rule.until, tz) : null,
  };
}

const PAD_MS = 24 * 60 * 60 * 1000;

/**
 * Guard band for {@link outOfRange}. In zone mode the expansion queries a
 * *floating* window padded ±1 day and filters back afterwards, so comparing the
 * rule's instant bounds against the requested window exactly could reject an
 * occurrence that the offset conversion would have pulled back inside. Two days
 * is wider than any real UTC offset shift.
 */
const BOUND_GUARD_MS = 2 * PAD_MS;

/**
 * Whether the rule provably has no occurrence in [from, to], judged from its own
 * dtstart/until without parsing or iterating the RRULE.
 *
 * A long-lived calendar accumulates hundreds of ENDED series (84% of the rules
 * in the reference dataset). Parsing each one's rrule and walking it only to
 * produce nothing dominated every windowed calendar expansion, so reject them
 * before `rrulestr` gets involved. `rule.until` is the EXCLUSIVE end.
 */
function outOfRange(rule: RecurrenceRule, from: Date, to: Date): boolean {
  if (rule.until && fromIso(rule.until).getTime() + BOUND_GUARD_MS <= from.getTime()) return true;
  return fromIso(rule.dtstart).getTime() - BOUND_GUARD_MS > to.getTime();
}

/**
 * Whether the series is over before `from`. Unlike {@link outOfRange} this looks
 * only at `until`, which the base rule imposes on EVERY segment — so a caller
 * can skip its per-segment work wholesale, not just the expansion.
 */
export function seriesEndsBefore(rule: RecurrenceRule, from: Date): boolean {
  return !!rule.until && fromIso(rule.until).getTime() + BOUND_GUARD_MS <= from.getTime();
}

/** All occurrences within [from, to] (inclusive), as true UTC instants. */
export function occurrencesBetween(rule: RecurrenceRule, from: Date, to: Date): Date[] {
  if (outOfRange(rule, from, to)) return [];
  if (!rule.tz) {
    return buildRRule(rule.rrule, fromIso(rule.dtstart), rule.until ? new Date(rule.until) : null)
      .between(from, to, true);
  }
  const tz = rule.tz;
  const { dtstart, until } = floatingView(rule, tz);
  const rr = buildRRule(rule.rrule, dtstart, until);
  // Query a floating window padded ±1 day so occurrences whose floating
  // position sits just outside the converted bounds (due to the zone offset)
  // are not clipped; we filter back to the true instant window afterwards.
  const fromF = new Date(instantToFloating(from.toISOString(), tz).getTime() - PAD_MS);
  const toF = new Date(instantToFloating(to.toISOString(), tz).getTime() + PAD_MS);
  const out: Date[] = [];
  for (const o of rr.between(fromF, toF, true)) {
    const inst = new Date(floatingToInstant(o, tz));
    if (inst >= from && inst <= to) out.push(inst);
  }
  return out;
}

/**
 * The last occurrence of a capped series (where `rule.until` is set), or null if
 * the series has no end date. Returns a true UTC instant.
 */
export function lastOccurrence(rule: RecurrenceRule): Date | null {
  if (!rule.until) return null;
  if (!rule.tz) {
    const r = rrulestr(rule.rrule, { dtstart: fromIso(rule.dtstart) });
    if (!(r instanceof RRule)) return null;
    // rule.until is exclusive — the last included occurrence is strictly before it.
    return r.before(fromIso(rule.until), false) ?? null;
  }
  const tz = rule.tz;
  const { dtstart } = floatingView(rule, tz);
  const r = rrulestr(rule.rrule, { dtstart });
  if (!(r instanceof RRule)) return null;
  const occF = r.before(instantToFloating(rule.until, tz), false);
  return occF ? new Date(floatingToInstant(occF, tz)) : null;
}

/** Next `n` occurrences strictly after `after`, as true UTC instants. */
export function nextNOccurrences(rule: RecurrenceRule, after: Date, n: number): Date[] {
  const tz = rule.tz;
  if (!tz) {
    const r = buildRRule(rule.rrule, fromIso(rule.dtstart), rule.until ? new Date(rule.until) : null);
    const out: Date[] = [];
    let cursor: Date | null = r.after(after, false);
    while (cursor && out.length < n) {
      out.push(cursor);
      cursor = r.after(cursor, false);
    }
    return out;
  }
  const { dtstart, until } = floatingView(rule, tz);
  const r = buildRRule(rule.rrule, dtstart, until);
  const out: Date[] = [];
  let cursor: Date | null = r.after(instantToFloating(after.toISOString(), tz), false);
  while (cursor && out.length < n) {
    out.push(new Date(floatingToInstant(cursor, tz)));
    cursor = r.after(cursor, false);
  }
  return out;
}
